import { type CSSProperties, useEffect, useRef, useState } from 'react';

import type { NavbarMenuListElementProps, NavbarMenuListGroupElementProps } from '@/navbarElement/Navbar.type';

import { cn } from '../../utils/cn';

import * as styles from '../Navbar.css';

const JUSTIFY_CLASS_MAP = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
};

const MAX_ITEM_COUNT_ASSUMED_TO_FIT = 13;

export function NavbarMenuListGroupV2({
  element,
  parent,
  className,
  children,
  style,
  attributes,
}: NavbarMenuListGroupElementProps & {
  parent?: NavbarMenuListElementProps['element'];
}) {
  const {
    type,
    justify,
    customAttributes,
    lineDividerStyle,
    lineDividerWidth,
    lineDividerHeight,
    lineDividerColor,
    gap,
    mobileGap,
  } = element.attrs || {};

  const showDivider = lineDividerStyle && lineDividerStyle !== 'none';

  const dividerStyle = showDivider
    ? ({
        '--line-divider-width': lineDividerWidth || '0.5px',
        '--line-divider-height': lineDividerHeight || '18px',
        '--line-divider-style': lineDividerStyle || 'solid',
        '--line-divider-color': lineDividerColor || '#000000',
        '--line-divider-gap': '8px',
      } as CSSProperties)
    : {};

  // Handle flex justification for middle type groups
  const flexJustify = type === 'middle' && justify ? JUSTIFY_CLASS_MAP[justify] : '';

  const baseStyle: CSSProperties = {
    '--min-height': 'var(--height)',
    ...(typeof gap === 'string' && gap !== '' && { '--gap': gap }),
    ...(typeof mobileGap === 'string' && mobileGap !== '' && { '--mobile-gap': mobileGap }),
    ...dividerStyle,
    ...style,
  };

  const isMiddleGroupCentered =
    parent?.content?.[1]?.attrs?.type === 'middle' &&
    parent.content[1].attrs?.justify === 'center' &&
    parent.content.length === 3;
  const shouldMeasureOverflow = type === 'middle' && isMiddleGroupCentered;

  const groupRef = useRef<HTMLDivElement>(null);
  // null until the first measurement, so server and first client render fall back to the item count.
  const [isOverflowing, setIsOverflowing] = useState<boolean | null>(null);

  useEffect(() => {
    const group = groupRef.current;
    if (!shouldMeasureOverflow || !group) return;

    // ResizeObserver callbacks run after layout, so reading scrollWidth here never forces a reflow.
    const resizeObserver = new ResizeObserver(() => {
      setIsOverflowing(group.scrollWidth > group.clientWidth);
    });
    // Descendants are observed because an item widening inside the fixed-width group does not resize it.
    const observeGroupAndItems = () => {
      resizeObserver.disconnect();
      for (const observedElement of [group, ...group.querySelectorAll('*')]) {
        resizeObserver.observe(observedElement);
      }
    };
    // Re-observing makes the ResizeObserver report again after the next layout, so live edits re-measure without a synchronous read.
    const mutationObserver = new MutationObserver(observeGroupAndItems);
    mutationObserver.observe(group, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
    observeGroupAndItems();

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [shouldMeasureOverflow]);

  const overFlowClasses = cn(
    '!overflow-hidden !overflow-x-auto',
    (isOverflowing ?? (element.content?.length ?? 0) > MAX_ITEM_COUNT_ASSUMED_TO_FIT) && '!justify-start',
  );

  const flexChildBehaviorClasses = isMiddleGroupCentered
    ? cn(
        shouldMeasureOverflow && cn(overFlowClasses, 'flex-grow'),
        type === 'left' && 'shrink-0 flex-1',
        type === 'right' && 'shrink-0 flex-1',
      )
    : cn(type === 'middle' && 'flex-1', type === 'left' && 'shrink-0', type === 'right' && 'shrink-0');

  const classes = cn(
    flexChildBehaviorClasses,
    styles.navbarMenuListGroupV2,
    type === 'left' && styles.alignLeftV2,
    type === 'middle' &&
      cn(
        justify === 'center' && styles.alignCenterV2,
        justify === 'start' && styles.alignLeftV2,
        justify === 'end' && styles.alignRightV2,
      ),
    type === 'right' && styles.alignRightV2,
    showDivider && 'navbar-divider-group',
    className,
  );

  return (
    <div
      {...attributes}
      {...customAttributes}
      style={baseStyle}
      className={cn(classes, flexJustify)}
      {...(showDivider && { 'data-line-divider': 'true' })}
      ref={groupRef}
    >
      {children}
    </div>
  );
}
